import { Gauge } from "lucide-react";
import { fmtDateTime } from "@/lib/format";
import { Panel } from "@/components/crm/Badges";

const LABELS = {
  respuesta: "Respuestas de vendedores", etiqueta: "Etiquetas", upsert: "Alta/actualización de contactos", contacto_nuevo: "Contactos nuevos",
  enriquecimiento: "Enriquecimiento", backfill: "Actualización masiva", adjunto: "Adjuntos", limpieza: "Limpieza", probar_conexion: "Probar conexión",
  contacto: "Consulta de contacto", otro: "Otros",
};
const n = (v) => (v == null ? "—" : Number(v).toLocaleString("es-EC"));
const ms = (v) => (v == null ? "—" : `${n(v)} ms`);
const GRID = "grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]";
const EVENT_LABELS = {
  InboundMessage: "Mensaje entrante", OutboundMessage: "Mensaje saliente", ConversationUnreadUpdate: "Conversación leída/no leída",
  ContactCreate: "Contacto creado", ContactUpdate: "Contacto actualizado", ContactDelete: "Contacto eliminado",
  ContactTagUpdate: "Etiquetas de contacto", INSTALL: "Instalación", UNINSTALL: "Desinstalación",
};
const eventLabel = (k) => EVENT_LABELS[k] || k;
const okTone = (p) => (p == null ? "text-[#0B2545]" : p < 95 ? "text-rose-600" : p < 99 ? "text-amber-600" : "text-emerald-600");

const Kpi = ({ label, value, sub, ratio, bar, testid, tone = "text-[#0B2545]" }) => (
  <div className="min-w-0 rounded-xl bg-[#F5F7FA] p-4" data-testid={testid}>
    <p className="text-[11px] font-medium uppercase leading-snug tracking-wide text-slate-500">{label}</p>
    <p className={`mt-1 whitespace-nowrap text-xl font-bold tabular-nums sm:text-2xl ${tone}`}>{value}</p>
    {sub && <p className="text-xs text-slate-500">{sub}</p>}
    {ratio != null && (
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200">
        <div className={`h-full rounded-full ${bar || (ratio < 0.2 ? "bg-rose-500" : ratio < 0.5 ? "bg-amber-500" : "bg-emerald-500")}`} style={{ width: `${Math.max(2, Math.min(100, ratio * 100))}%` }} />
      </div>
    )}
  </div>
);

const Chip = ({ ok, children, testid }) => (
  <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${ok ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-800"}`} data-testid={testid}>{children}</span>
);

const Breakdown = ({ rows, testid, emptyText, label = (k) => k }) => {
  const total = rows.reduce((a, [, v]) => a + v, 0);
  if (!rows.length) return <p className="mt-2 text-sm text-slate-400" data-testid={`${testid}-empty`}>{emptyText}</p>;
  return (
    <ul className="mt-2 max-w-2xl divide-y divide-slate-100 text-sm" data-testid={testid}>
      {rows.map(([k, v]) => (
        <li key={k} className="flex items-center gap-3 py-1.5">
          <span className="min-w-0 flex-1 truncate" title={k === label(k) ? k : `${label(k)} (${k})`}>{label(k)}</span>
          <span className="shrink-0 tabular-nums font-semibold">{n(v)}</span>
          <span className="w-12 shrink-0 text-right tabular-nums text-xs font-medium text-slate-600">{total ? Math.round((100 * v) / total) : 0}%</span>
        </li>
      ))}
    </ul>
  );
};

const sorted = (o) => Object.entries(o || {}).sort((a, b) => b[1] - a[1]);

export const PlatformUsagePanel = ({ usage, webhooks }) => {
  if (!usage) return null;
  const l = usage.limits || {};
  const dailyRatio = l.daily_limit ? l.daily_remaining / l.daily_limit : null;
  const burstRatio = l.burst_max ? l.burst_remaining / l.burst_max : null;
  return (
    <Panel title="Cuota de la plataforma" icon={Gauge} className="xl:col-span-3" testid="platform-usage-panel">
      {usage.preview && <p className="mb-3 rounded-xl bg-amber-50 p-3 text-sm text-amber-800" data-testid="usage-preview-note">Vista previa: las llamadas y tareas en segundo plano contra la plataforma real están desactivadas (solo «Probar conexión»).</p>}
      {usage.paused_until && <p className="mb-3 rounded-xl bg-rose-50 p-3 text-sm text-rose-700" data-testid="usage-paused">Límite alcanzado: tareas en pausa hasta {fmtDateTime(usage.paused_until)}.</p>}
      <div className={GRID}>
        <Kpi label="Cuota diaria restante" value={n(l.daily_remaining)} sub={l.daily_limit ? `de ${n(l.daily_limit)}` : "Sin datos aún"} ratio={dailyRatio} testid="usage-daily-remaining" />
        <Kpi label="Ráfaga restante (10 s)" value={n(l.burst_remaining)} sub={l.burst_max ? `de ${n(l.burst_max)}` : "Sin datos aún"} ratio={burstRatio} testid="usage-burst-remaining" />
        <Kpi label="Peticiones última hora" value={n(usage.last_hour?.total ?? 0)} sub={`${n(usage.last_24h_total)} en 24 h`} testid="usage-last-hour" />
        <Kpi label="Errores 429 (24 h)" value={n(usage.last_24h_429 ?? 0)} testid="usage-429" tone={usage.last_24h_429 ? "text-rose-600" : "text-[#0B2545]"} />
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Chip ok testid="usage-token-source">Credencial: {usage.token_source === "oauth" ? "App instalada (cuota propia)" : "Token privado"}</Chip>
        <Chip ok={usage.background_allowed} testid="usage-background">Tareas en segundo plano: {usage.background_allowed ? "activas" : "en pausa"}</Chip>
        <Chip ok={!usage.messages_waiting_retry} testid="usage-retry-waiting">Mensajes esperando reenvío: {n(usage.messages_waiting_retry)}</Chip>
      </div>
      <div className="mt-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Peticiones última hora por tipo</p>
        <Breakdown rows={sorted(usage.last_hour?.counts)} testid="usage-by-type" emptyText="Sin peticiones en la última hora." label={(k) => LABELS[k] || k} />
      </div>
      {l.updated_at && <p className="mt-2 text-xs text-slate-400">Límites leídos {fmtDateTime(l.updated_at)}</p>}
      {webhooks && <WebhookStats w={webhooks} />}
    </Panel>
  );
};

const WebhookStats = ({ w }) => (
  <div className="mt-6 border-t border-slate-100 pt-5" data-testid="webhook-stats">
    <p className="text-sm font-semibold text-[#0B2545]">Webhooks últimas 24 h</p>
    <div className={`mt-3 ${GRID}`}>
      <Kpi label="Recibidos" value={n(w.total)} testid="wh-total" />
      <Kpi label="Respuestas 2xx" value={w.ok_pct == null ? "—" : `${n(w.ok_pct)} %`} ratio={w.ok_pct == null ? null : w.ok_pct / 100} testid="wh-ok-pct"
           tone={okTone(w.ok_pct)} bar={okTone(w.ok_pct).replace("text-", "bg-").replace("-600", "-500").replace("[#0B2545]", "slate-400")} />
      <Kpi label="Rechazados 401" value={n(w.s401)} testid="wh-401" />
      <Kpi label="Errores 5xx" value={n(w.s5xx)} testid="wh-5xx" tone={w.s5xx ? "text-rose-600" : "text-[#0B2545]"} />
      <Kpi label="Latencia p50" value={ms(w.p50)} testid="wh-p50" />
      <Kpi label="Latencia p95" value={ms(w.p95)} testid="wh-p95" tone={w.p95 > 500 ? "text-amber-600" : "text-[#0B2545]"} />
    </div>
    <p className="mt-5 text-xs font-semibold uppercase tracking-wide text-slate-500">Por tipo de evento</p>
    <Breakdown rows={sorted(w.by_type)} testid="wh-by-type" label={eventLabel} emptyText="Sin entregas en las últimas 24 h." />
    {w.spooled > 0 && <p className="mt-2 text-xs text-amber-700" data-testid="wh-spooled">{n(w.spooled)} eventos en cola local esperando la base de datos</p>}
  </div>
);
